import type {
  CottageContactWhatsAppPageCreateForm,
  CottageContactWhatsAppPageCreateResponse,
} from '../entities/CottageContactWhatsAppPageCreate'

import type {
  CottageContactWhatsAppPagePricesDates,
  CottageContactWhatsAppPagePricesPeople,
} from '../entities/CottageContactWhatsAppPagePrices'

import type { CottageContactWhatsAppPage } from '../entities/CottageContactWhatsAppPage'
import fetchContact from '~~/modules/api/rurality/v3/contacts/new'
import { getDataMining } from '~~/modules/integrations/datamining/composables/useDataMiningComposable'
import { type paramsType } from '~~/modules/api/rurality/v3/contacts/new/paramsSchema'

type responseType = {
  apiResponseSuccess: CottageContactWhatsAppPageCreateResponse | null
  apiResponseError: ApiResponseError | null
}

export function useCottageContactWhatsAppPage() {
  const formatDateString = (
    dates: CottageContactWhatsAppPagePricesDates | null,
  ) => {
    if (dates && dates.start && dates.end) {
      const startString = toRuralityDate().displayDate(dates.start as Date)
      const endString = toRuralityDate().displayDate(dates.end as Date)

      return `${startString} - ${endString}`
    } else {
      return ''
    }
  }

  async function sendFormData(params: {
    createForm: CottageContactWhatsAppPageCreateForm
    CottageContactWhatsAppPage: CottageContactWhatsAppPage
    dates: CottageContactWhatsAppPagePricesDates | null
    people: CottageContactWhatsAppPagePricesPeople | null
  }): Promise<responseType> {
    const dataMining = getDataMining()

    const responseCottagePage: responseType = {
      apiResponseSuccess: null,
      apiResponseError: null,
    }

    const showInputDate = formatDateString(params.dates)

    let finalMessage = ''

    if (showInputDate && params.people) {
      finalMessage = t(
        'views.cottage-contact-whatsapp.form.finalMessage.dateAndPeople',
        {
          date: showInputDate,
          people: params.people,
          message: params.createForm.message,
        },
      )
    } else if (showInputDate) {
      finalMessage = t(
        'views.cottage-contact-whatsapp.form.finalMessage.date',
        {
          date: showInputDate,
          message: params.createForm.message,
        },
      )
    } else if (params.people) {
      finalMessage = t(
        'views.cottage-contact-whatsapp.form.finalMessage.people',
        {
          people: params.people,
          message: params.createForm.message,
        },
      )
    } else {
      finalMessage = params.createForm.message
    }

    const queryParams: paramsType = {
      source: 'ESCAPADA_RURAL',
      cottageId: params.CottageContactWhatsAppPage.id,
      traveler: {
        name: params.createForm.name,
        email: params.createForm.email,
        phone: params.createForm.phone,
        allowComms: params.createForm.allowComms,
      },
      message: finalMessage,
      kpisData: dataMining,
      channel: 'WHATSAPP',
    }

    const contactResponse = await fetchContact(queryParams)

    if (contactResponse?.success) {
      const CottageContactWhatsAppPageCreate: CottageContactWhatsAppPageCreateResponse =
        {
          id: contactResponse.success.data.id,
          channel: contactResponse.success.data.channel,
          message: contactResponse.success.data.message,
          createdAt: contactResponse.success.data.createdAt,
          owner: {
            id: contactResponse.success.data.ownerId,
          },
          traveler: {
            id: contactResponse.success.data.traveler.id,
            name: contactResponse.success.data.traveler.name,
            email: contactResponse.success.data.traveler.email,
            phone: contactResponse.success.data.traveler.phone,
          },
          cottage: {
            id: contactResponse.success.data.cottageId,
          },
        }
      responseCottagePage.apiResponseSuccess = CottageContactWhatsAppPageCreate
    } else if (contactResponse.error) {
      responseCottagePage.apiResponseError =
        contactResponse.error as ApiResponseError
    }

    return responseCottagePage
  }

  return {
    sendFormData,
  }
}
